import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import {
  Mail, Lock, Eye, EyeOff, ShieldCheck, Sparkles, BookOpen, Heart,
  Loader2, ChevronRight, GraduationCap, Users,
} from "lucide-react";

/**
 * Auth shell  -  EZducate-inspired split screen.
 * Left: dark navy with brand identity, mission, and 3-4 trust cards.
 * Right: white card with logo, heading, "Continue with Google" button,
 *        divider, email + password (with show/hide), forgot link, sign-in.
 *
 * Visual constants align with new design direction:
 *   navy   #0F1830 (deep brand backdrop)
 *   accent #81B29A (sage, primary action overlay)
 *   coral  #E07A5F (primary CTA)
 *   amber  #F2CC8F (small badges)
 */

const TRUST_CARDS = [
  {
    icon: BookOpen,
    title: "17 clinical frameworks",
    body: "Carol Gray 10.4 (2023), PBIS, TEACCH, UDL 3.0, NICE NG87. Every output is rule-checked.",
  },
  {
    icon: Sparkles,
    title: "SpectraMind AI engine",
    body: "Generates IEP goals, social stories and worksheets that are condition-specific and child-specific.",
  },
  {
    icon: Heart,
    title: "Built by a practitioner",
    body: "RCI-certified special educator with 10+ years of classroom experience designed every prompt.",
  },
  {
    icon: ShieldCheck,
    title: "FERPA + GDPR aware",
    body: "End-to-end encryption, signed DPA for schools, audited subprocessor list.",
  },
];

function Shell({ heading, subheading, children }) {
  return (
    <>
      <SEOHead
        title="Sign in to SpectraLearn"
        description="Sign in or create your SpectraLearn account."
        noindex
      />

      <Navbar />
      <div className="min-h-[calc(100vh-80px)] flex" style={{ background: "#FEF4EE" }} data-testid="auth-shell">
        {/* LEFT - warm welcoming panel (hidden on mobile). iter175: swapped
            dark navy for a bright cream+peach+mint panel so the first
            impression matches Kiddo+ style guide the user requested. */}
        <div
          className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-between p-10 xl:p-14 gap-10"
          style={{ background: "linear-gradient(160deg, #FFE7D6 0%, #E6F7F4 100%)" }}
        >
          {/* Soft blobs */}
          <div aria-hidden className="absolute -top-32 -left-32 h-96 w-96 rounded-full opacity-60" style={{ background: "radial-gradient(circle, #FFDCC5 0%, transparent 70%)" }} />
          <div aria-hidden className="absolute -bottom-40 -right-32 h-96 w-96 rounded-full opacity-40" style={{ background: "radial-gradient(circle, #A7EFE6 0%, transparent 70%)" }} />

          <div className="relative z-10 max-w-md">
            <span
              className="inline-block text-[10px] font-black tracking-widest uppercase rounded-full px-3 py-1 mb-5"
              style={{ background: "#FFF2C6", color: "#8A6D00" }}
            >
              ★ Made with care for every kind of mind
            </span>
            <h2
              className="text-3xl xl:text-4xl font-black leading-[1.05]"
              style={{ color: "#1B1F3B", fontFamily: '"DM Sans","Inter",sans-serif' }}
            >
              Every session today shapes a{" "}
              <span style={{ color: "#F97066", fontFamily: '"DM Sans","Inter",sans-serif' }}>
                brighter tomorrow
              </span>
              .
            </h2>
            <p className="mt-4 text-[14px] leading-relaxed" style={{ color: "#4B5162" }}>
              Personalised social stories, adaptive worksheets, and gentle
              nudges - built with clinicians so you always know the next kind step.
            </p>

            <div className="mt-6 rounded-2xl bg-white/95 p-4 shadow-md" data-testid="auth-preview-card">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full text-white font-black" style={{ background: "#F97066" }}>A</div>
                <div className="flex-1">
                  <div className="text-[13px] font-bold" style={{ color: "#1B1F3B" }}>Today for Aarav, 7y</div>
                </div>
                <span className="rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-wider" style={{ background: "#E6F7F4", color: "#0A7A6E" }}>On track</span>
              </div>
              {[
                { label: "Reading",   pct: 78, tint: "#F97066" },
                { label: "Focus",     pct: 64, tint: "#0FB5A5" },
                { label: "Behaviour", pct: 52, tint: "#7C3AED" },
              ].map((r) => (
                <div key={r.label} className="mt-3 flex items-center gap-3">
                  <div className="w-20 text-[11px] font-semibold" style={{ color: "#4B5162" }}>{r.label}</div>
                  <div className="flex-1 overflow-hidden rounded-full bg-[#F1E9DE]">
                    <div className="h-2 rounded-full" style={{ width: `${r.pct}%`, background: r.tint }} />
                  </div>
                  <div className="w-8 text-right text-[11px] font-bold" style={{ color: "#1B1F3B" }}>{r.pct}%</div>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              {TRUST_CARDS.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  data-testid={`auth-trust-${title.split(" ")[0].toLowerCase()}`}
                  className="rounded-2xl border bg-white/95 p-3.5"
                  style={{ borderColor: "#F2D9C6" }}
                >
                  <Icon className="h-4 w-4 mb-1.5" style={{ color: "#0A7A6E" }} aria-hidden="true" />
                  <h3 className="font-black text-[13px]" style={{ color: "#1B1F3B" }}>{title}</h3>
                  <p className="text-[11.5px] mt-1 leading-relaxed" style={{ color: "#4B5162" }}>{body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative z-10 flex items-center gap-4 text-xs mt-8 pt-6 border-t" style={{ color: "#4B5162", borderColor: "#F2D9C6" }}>
            <span>© SpectraLearn Inc.</span>
            <span aria-hidden>·</span>
            <span>Toronto, ON</span>
            <span aria-hidden>·</span>
            <Link to="/privacy" className="hover:underline" style={{ color: "#1B1F3B" }}>Privacy</Link>
          </div>
        </div>

        {/* RIGHT - auth card */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-[440px]">
            <div className="bg-white rounded-3xl shadow-[0_10px_40px_-15px_rgba(15,24,48,0.12)] p-7 sm:p-9 border" style={{ borderColor: "#F2D9C6" }}>
              <h1 className="text-3xl font-black" style={{ color: "#1B1F3B", fontFamily: '"DM Sans","Inter",sans-serif' }}>{heading}</h1>
              <p className="mt-1.5 text-[15px]" style={{ color: "#4B5162" }}>{subheading}</p>
              <div className="mt-7">{children}</div>
            </div>
            <p className="text-center text-xs mt-5 leading-relaxed" style={{ color: "#7A827C" }}>
              By continuing, you agree to our{" "}
              <Link to="/terms" className="underline underline-offset-2" style={{ color: "#1B1F3B" }}>Terms</Link> and{" "}
              <Link to="/privacy" className="underline underline-offset-2" style={{ color: "#1B1F3B" }}>Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
function GoogleIcon({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
    </svg>
  );
}

function FormField({ label, icon: Icon, type = "text", value, onChange, placeholder, testid, right, autoComplete }) {
  return (
    <div className="min-w-0">
      <label className="block text-[13px] font-semibold text-[#3A3F3B] mb-1.5">{label}</label>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A827C]" aria-hidden="true" />
        )}
        <input
          type={type}
          required
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          data-testid={testid}
          className={`w-full h-12 ${Icon ? "pl-10" : "pl-3.5"} ${right ? "pr-11" : "pr-3.5"}
            rounded-xl border border-[#2D312E]/15 bg-white text-[15px]
            focus:border-[#4F7660] focus:ring-2 focus:ring-[#81B29A]/15 focus:outline-none
            placeholder:text-[#A8AFAB] transition-colors`}
        />
        {right}
      </div>
    </div>
  );
}

function GoogleButton({ disabled, mode = "login", next, role }) {
  const [checking, setChecking] = useState(false);
  const handleGoogle = async () => {
    if (checking) return;
    setChecking(true);
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      // Quick probe so we never dump the user on a cold-start splash followed
      // by a raw JSON error when GOOGLE_CLIENT_ID is missing in the env.
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/status`, { cache: "no-store" });
      const j = r.ok ? await r.json() : { configured: false };
      if (!j.configured) {
        toast.message("Google sign-in is being set up", {
          description: "Please use email and password for now. We'll enable Google sign-in shortly.",
        });
        setChecking(false);
        return;
      }
      const params = new URLSearchParams();
      if (next) params.set("next", next);
      if (role) params.set("role", role);
      const qs = params.toString() ? `?${params.toString()}` : "";
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/start${qs}`;
    } catch {
      toast.error("Could not reach the sign-in service. Try email/password.");
      setChecking(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleGoogle}
      disabled={disabled || checking}
      data-testid="auth-google-btn"
      className="w-full h-12 inline-flex items-center justify-center gap-3 rounded-xl border border-[#2D312E]/15 bg-white text-[15px] font-medium text-[#2D312E] hover:bg-[#F4F1EA] disabled:opacity-60 transition-colors"
    >
      <GoogleIcon /> {checking ? "Checking…" : (mode === "register" ? "Sign up with Google" : "Continue with Google")}
    </button>
  );
}

function OrDivider({ label = "or" }) {
  return (
    <div className="flex items-center my-5" aria-hidden="true">
      <div className="flex-1 h-px bg-[#2D312E]/10" />
      <span className="px-3 text-xs text-[#7A827C]">{label}</span>
      <div className="flex-1 h-px bg-[#2D312E]/10" />
    </div>
  );
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next");

  // iter181.1 - Post-login perceived latency was dominated by lazy-loading
  // the dashboard chunk AFTER the auth POST resolved. Kick off dashboard
  // chunk downloads the moment the user lands on /login so the module is
  // hot by the time navigate() fires. Fire-and-forget; failures are silent
  // (the actual navigation will retry the import as usual).
  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
    idle(() => {
      // Both parent + educator dashboards - we don't know the role yet.
      import("@/pages/parent/ParentDashboard").catch(() => {});
      import("@/pages/educator/EducatorDashboard").catch(() => {});
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      if (next) { nav(next); return; }
      const redirectByRole = (role) => {
        if (role === "admin")     return "/admin/dashboard";
        if (role === "educator")  return "/educator";
        if (role === "therapist") return "/therapist/dashboard";
        return "/parent";
      };
      nav(redirectByRole(u.role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <Shell heading="Welcome back" subheading="Sign in to continue to your dashboard.">
      <GoogleButton disabled={loading} mode="login" next={next} />
      <OrDivider />

      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <FormField
          label="Email address" icon={Mail} type="email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@school.edu" testid="login-email-input"
          autoComplete="email"
        />
        <FormField
          label="Password" icon={Lock} type={showPwd ? "text" : "password"}
          value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password" testid="login-password-input"
          autoComplete="current-password"
          right={(
            <button type="button"
              onClick={() => setShowPwd((v) => !v)}
              data-testid="login-show-password"
              aria-label={showPwd ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center text-[#7A827C] hover:text-[#2D312E]">
              {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
        />
        <div className="flex justify-end">
          <Link to="/forgot-password" data-testid="login-forgot-link" className="text-[13px] text-[#0F1830] hover:text-[#E07A5F] font-medium">
            Forgot password?
          </Link>
        </div>
        <button type="submit" disabled={loading} data-testid="login-submit-btn"
          className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full text-white font-black text-[15px] disabled:opacity-60 transition-all hover:-translate-y-0.5 shadow-md"
          style={{ background: "linear-gradient(180deg, #F97066 0%, #E5533E 100%)" }}>
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : <>Sign in <ChevronRight className="h-4 w-4" /></>}
        </button>
      </form>

      <p className="mt-6 text-[14px] text-[#5C635E] text-center">
        New to SpectraLearn?{" "}
        <Link to={`/register${next ? `?next=${encodeURIComponent(next)}` : ""}`}
          data-testid="login-to-register-link"
          className="font-black hover:underline">
          Create a free account
        </Link>
      </p>
    </Shell>
  );
}

import ConditionDataConsent from "@/components/auth/ConditionDataConsent";
import api from "@/lib/api";

import SEOHead from "@/components/SEOHead";
export function RegisterPage() {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "parent" });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  // iter153 Action 10 - EU visitors see the GDPR Article 9 consent step
  // between "Create account" and their dashboard. Non-EU visitors bypass it.
  const [showEUConsent, setShowEUConsent] = useState(false);
  const [pendingNav, setPendingNav] = useState(null);
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const finishNav = () => {
    if (pendingNav) { nav(pendingNav); return; }
    // Fallback (should never hit).
    nav("/parent");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success("Account created");
      const target = next || (u.role === "educator" ? "/educator" : "/parent");
      // iter153 Action 10 - detect EU country via /api/geoip.
      let inEU = false;
      try {
        const r = await api.get("/geoip", { timeout: 4000 });
        // Cloudflare / ip-api country codes for EU + UK + EEA.
        const EU_LIKE = new Set([
          "GB", "IE", "DE", "FR", "IT", "ES", "NL", "BE", "AT", "SE", "DK",
          "FI", "PT", "GR", "PL", "CZ", "SK", "HU", "RO", "BG", "HR", "SI",
          "EE", "LV", "LT", "LU", "MT", "CY", "IS", "NO", "LI",
        ]);
        inEU = EU_LIKE.has((r.data?.country || "").toUpperCase());
      } catch (_) { /* fail open: no consent gate on geoip errors */ }
      if (inEU) {
        setPendingNav(target);
        setShowEUConsent(true);
      } else {
        if (next) { nav(next); } else { nav(target); }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  if (showEUConsent) {
    return (
      <Shell heading="One more step" subheading="Because you are in the EU, we need your explicit permission to store learning-condition information.">
        <ConditionDataConsent
          onAccept={() => { toast.success("Consent recorded"); finishNav(); }}
          onDecline={() => { toast("You can enable condition personalisation later in Account Settings."); finishNav(); }}
        />
      </Shell>
    );
  }

  return (
    <Shell heading="Create your free account" subheading="Start creating instantly - 5 free clinical SpectraMind generations to build IEP-aligned lessons, visual stories, and worksheets. No credit card required.">
      <GoogleButton disabled={loading} mode="register" next={next} role={form.role} />
      <OrDivider />

      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <FormField
          label="Full name" icon={Users} type="text"
          value={form.name} onChange={update("name")}
          placeholder="Your name" testid="register-name-input"
          autoComplete="name"
        />
        <FormField
          label="Email address" icon={Mail} type="email"
          value={form.email} onChange={update("email")}
          placeholder="you@school.edu" testid="register-email-input"
          autoComplete="email"
        />
        <FormField
          label="Password" icon={Lock} type={showPwd ? "text" : "password"}
          value={form.password} onChange={update("password")}
          placeholder="At least 8 characters" testid="register-password-input"
          autoComplete="new-password"
          right={(
            <button type="button" onClick={() => setShowPwd((v) => !v)} data-testid="register-show-password"
              aria-label={showPwd ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center text-[#7A827C] hover:text-[#2D312E]">
              {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
        />

        <div className="min-w-0">
          <label className="block text-[13px] font-semibold text-[#3A3F3B] mb-1.5">I am a…</label>
          <div className="grid grid-cols-2 gap-2.5" role="radiogroup" aria-label="Role">
            {[
              { value: "parent",   label: "Parent",   icon: Heart },
              { value: "educator", label: "Educator", icon: GraduationCap },
            ].map(({ value, label, icon: Icon }) => {
              const active = form.role === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-testid={`register-role-${value}`}
                  onClick={() => setForm((f) => ({ ...f, role: value }))}
                  className={`h-12 rounded-xl border-2 inline-flex items-center justify-center gap-2 text-[14px] font-medium transition-colors
                    ${active ? "border-[#4F7660] bg-[#E8F0EC] text-[#2D312E]" : "border-[#2D312E]/15 bg-white text-[#5C635E] hover:border-[#4F7660]/50"}`}
                >
                  <Icon className="h-4 w-4" /> {label}
                </button>
              );
            })}
          </div>
        </div>

        <p className="text-xs text-[#5C635E] leading-relaxed text-center" data-testid="register-legal-consent">
          By creating an account, you agree to our{" "}
          <Link to="/terms" data-testid="register-terms-link" className="text-[#0D3D6B] underline">Terms of Service</Link>
          {" "}and{" "}
          <Link to="/privacy" data-testid="register-privacy-link" className="text-[#0D3D6B] underline">Privacy Policy</Link>.
        </p>

        <button type="submit" disabled={loading} data-testid="register-submit-btn"
          className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full text-white font-black text-[15px] disabled:opacity-60 transition-all hover:-translate-y-0.5 shadow-md"
          style={{ background: "linear-gradient(180deg, #F97066 0%, #E5533E 100%)" }}>
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating account…</> : <>Create free account <ChevronRight className="h-4 w-4" /></>}
        </button>
      </form>

      <p className="mt-6 text-[14px] text-[#5C635E] text-center">
        Already have an account?{" "}
        <Link to={`/login${next ? `?next=${encodeURIComponent(next)}` : ""}`}
          data-testid="register-to-login-link"
          className="font-black hover:underline">
          Sign in
        </Link>
      </p>
    </Shell>
  );
}
